// Public web-chat client. Failures resolve so the page can fall back
// instead of showing a raw error.

import { apiFetch } from "../lib/api";

import type {
  ChatEmbed,
  ChatPart,
  ChatTurnResult,
  ChatWidgetPart,
} from "./types";

export async function postWebChat(input: {
  path?: string;
  messages: ChatPart[];
}): Promise<ChatTurnResult> {
  try {
    const r = await apiFetch(
      input.path ?? "/chats",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages: input.messages }),
      },
      { retries: 0 },
    );
    if (!r.ok) return { ok: false };
    return readTurnResult(await r.json());
  } catch {
    return { ok: false };
  }
}

export async function streamWebChat(input: {
  path?: string;
  messages: ChatPart[];
  onDelta?: (text: string) => void;
}): Promise<ChatTurnResult> {
  try {
    const r = await apiFetch(
      input.path ?? "/chats/stream",
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "text/event-stream",
        },
        body: JSON.stringify({ messages: input.messages }),
      },
      { retries: 0 },
    );
    if (r.status === 429) return { ok: false };
    const type = r.headers.get("content-type") ?? "";
    if (!r.ok || !type.includes("text/event-stream") || !r.body) {
      return postWebChat(input);
    }
    return readChatSse(r, input.onDelta);
  } catch {
    return postWebChat(input);
  }
}

export async function readChatSse(
  response: Response,
  onDelta?: (text: string) => void,
): Promise<ChatTurnResult> {
  let last: ChatTurnResult = { ok: false };
  await readSseJson(response, (data) => {
    const event = readStreamEvent(data);
    if (!event) return;
    if (event.type === "delta") {
      if (event.text) onDelta?.(event.text);
      return;
    }
    if (event.type === "error") {
      last = { ok: false };
      return;
    }
    last = event.result;
  });
  return last;
}

export async function readSseJson(
  response: Response,
  onEvent: (data: unknown) => void,
): Promise<void> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";
      for (const block of parts) {
        const parsed = parseSseBlock(block);
        if (parsed !== undefined) onEvent(parsed);
      }
    }
    const tail = parseSseBlock(buffer);
    if (tail !== undefined) onEvent(tail);
  } finally {
    reader.releaseLock();
  }
}

export function parseSseBlock(block: string): unknown | undefined {
  const data = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n")
    .trim();
  if (!data) return undefined;
  try {
    return JSON.parse(data) as unknown;
  } catch {
    return undefined;
  }
}

function readTurnResult(data: unknown): ChatTurnResult {
  if (typeof data !== "object" || data === null) return { ok: false };
  const body = data as {
    reply?: unknown;
    embed?: unknown;
    widgets?: unknown;
  };
  const widgets = readWidgets(body.widgets);
  const embed = readEmbed(body.embed);
  const reply = typeof body.reply === "string" ? body.reply.trim() : "";
  if (!reply && !embed && widgets.length === 0) return { ok: false };
  return {
    ok: true,
    reply,
    ...(embed ? { embed } : {}),
    ...(widgets.length > 0 ? { widgets } : {}),
  };
}

function readStreamEvent(raw: unknown):
  | { type: "delta"; text: string }
  | { type: "error" }
  | { type: "done"; result: ChatTurnResult }
  | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const type = (raw as { type?: unknown }).type;
  if (type === "delta") {
    const text = (raw as { text?: unknown }).text;
    return { type: "delta", text: typeof text === "string" ? text : "" };
  }
  if (type === "error") return { type: "error" };
  if (type === "done") {
    const result = readTurnResult(raw);
    return { type: "done", result };
  }
  return undefined;
}

function readWidgets(raw: unknown): ChatWidgetPart[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const widget = readWidget(entry);
    return widget ? [widget] : [];
  });
}

function readWidget(raw: unknown): ChatWidgetPart | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const part = raw as {
    type?: unknown;
    id?: unknown;
    tool?: unknown;
    input?: unknown;
    embed?: unknown;
    dismissed?: unknown;
  };
  if (part.type !== undefined && part.type !== "widget") return undefined;
  const id = typeof part.id === "string" ? part.id.trim() : "";
  const tool = typeof part.tool === "string" ? part.tool.trim() : "";
  const embed = readEmbed(part.embed);
  if (!id || !tool || !embed) return undefined;
  return {
    type: "widget",
    id,
    tool,
    input: part.input,
    embed,
    ...(part.dismissed === true ? { dismissed: true } : {}),
  };
}

function readEmbed(raw: unknown): ChatEmbed | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const kind = (raw as { kind?: unknown }).kind;
  if (typeof kind !== "string" || !kind.trim()) return undefined;
  return raw as ChatEmbed;
}
